import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import SearchableDropdown from '../Components/SearchableDropdown';
import { registerUser } from '../store/userSlice';
import { registerHospital } from '../store/hospitalSlice';
import { useRegion } from '../context/RegionContext';
import {
  getBasePathForRegion,
  getRegionCountryOptions,
  getRegionCountryRecord,
  getRegionCityOptions,
  getRegionOptions,
  getRegionStateLabel,
  getRegionStateOptions,
  getRegionStateRecord,
  getRegionFlagUrlForRegion,
} from '../Utils/region';

const Registration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { region } = useRegion();
  const regionOptions = useMemo(() => getRegionOptions(), []);
  const [searchParams] = useSearchParams();
  const isRegionConfirmed = useMemo(() => searchParams.get('step') === 'form', [searchParams]);
  const regionFlag = useMemo(
    () => (region === 'af' ? '/assets/images/africamap.jpg' : getRegionFlagUrlForRegion(region)),
    [region]
  );

  // ── Candidate (Job Seeker) Form ────────────────────────────────────────
 const [userForm, setUserForm] = useState({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    const { firstName, lastName, email, password, confirmPassword } = userForm;

    // Client-side validation
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = 'First name is required';
    if (!lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!email.trim()) newErrors.email = 'Email is required';
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setError('Please fix the errors above');
      setLoading(false);
      return;
    }

    try {
      await dispatch(
        registerUser({
          firstName,
          lastName,
          email,
          password,
          // confirmPassword is NOT sent to backend
        })
      ).unwrap();

      toast.success('Registration successful! Check your email for OTP verification.');
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (err) {
      console.error('Registration failed:', err);

      let msg = 'Registration failed. Please try again.';
      msg = typeof err === 'string' ? err : err?.message || msg;

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Password visibility toggle
  const togglePasswordVisibility = (fieldId) => {
    const input = document.getElementById(fieldId);
    const icon = document.getElementById(`toggle${fieldId.charAt(0).toUpperCase() + fieldId.slice(1)}`);
    if (!input || !icon) return;

    if (input.type === 'password') {
      input.type = 'text';
      icon.classList.replace('bi-eye-slash', 'bi-eye');
    } else {
      input.type = 'password';
      icon.classList.replace('bi-eye', 'bi-eye-slash');
    }
  };

  if (!isRegionConfirmed) {
    return (
      <div className="register-area pt-120 mb-120">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-xl-7">
              <div className="form-wrapper">
                <div className="form-title text-center">
                  <h3>Select Your Region</h3>
                  <span></span>
                  <p style={{ marginTop: 12, color: '#6b7280' }}>
                    Choose your region to continue with registration.
                  </p>
                </div>
                <div className="row g-4">
                  {regionOptions.map((option) => {
                    const optionFlag =
                      option.value === 'af' ? '/assets/images/africamap.jpg' : getRegionFlagUrlForRegion(option.value);
                    return (
                    <div className="col-md-6" key={option.value}>
                      <button
                        type="button"
                        className="primry-btn-2 w-100"
                        style={{
                          height: 64,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 12,
                        }}
                        onClick={() => {
                          const basePath = getBasePathForRegion(option.value);
                          const path = `${basePath}/register?step=form`;
                          window.location.assign(path);
                        }}
                      >
                        {optionFlag ? (
                          <img
                            src={optionFlag}
                            alt={`${option.label} flag`}
                            style={{
                              width: option.value === 'af' ? 34 : 28,
                              height: option.value === 'af' ? 24 : 20,
                              objectFit: 'cover',
                              borderRadius: 4,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                            }}
                          />
                        ) : null}
                        <span>{option.label}</span>
                      </button>
                    </div>
                  )})}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-area pt-120 mb-120">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-xl-7">
            <div className="form-wrapper">
              <div className="form-title text-center">
                <h3>Register Account</h3>
                <span></span>
              </div>
              {regionFlag ? (
                <>
                  <style>
                    {`
                      .hpwp-flag-hero {
                        position: relative;
                        border-radius: 18px;
                        overflow: hidden;
                        border: 1px solid rgba(15, 36, 85, 0.12);
                        background: linear-gradient(135deg, rgba(201, 154, 24, 0.08), rgba(15, 36, 85, 0.08));
                        box-shadow: 0 16px 40px rgba(15, 36, 85, 0.12);
                      }
                      .hpwp-flag-hero::after {
                        content: "";
                        position: absolute;
                        inset: 0;
                        background: linear-gradient(
                          120deg,
                          rgba(255, 255, 255, 0.35),
                          rgba(255, 255, 255, 0.05),
                          rgba(255, 255, 255, 0.35)
                        );
                        transform: translateX(-120%);
                        animation: flagSheen 3.8s ease-in-out infinite;
                        pointer-events: none;
                      }
                      .hpwp-flag-inner {
                        display: grid;
                        grid-template-columns: 160px 1fr;
                        align-items: center;
                        gap: 18px;
                        padding: 16px 22px;
                      }
                      .hpwp-flag-badge {
                        width: 160px;
                        height: 104px;
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 10px 24px rgba(15, 36, 85, 0.18);
                        position: relative;
                      }
                      .hpwp-flag-badge img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        display: block;
                        transform-origin: left center;
                        animation: flagWave 2.6s ease-in-out infinite;
                      }
                      .hpwp-flag-title {
                        margin: 0;
                        font-weight: 700;
                        letter-spacing: 0.3px;
                        color: var(--title-color1);
                      }
                      .hpwp-flag-sub {
                        margin: 6px 0 0;
                        color: #5c6370;
                        font-size: 14px;
                      }
                      @keyframes flagWave {
                        0% { transform: perspective(240px) rotateY(0deg); }
                        50% { transform: perspective(240px) rotateY(-10deg); }
                        100% { transform: perspective(240px) rotateY(0deg); }
                      }
                      @keyframes flagSheen {
                        0% { transform: translateX(-120%); }
                        55% { transform: translateX(120%); }
                        100% { transform: translateX(120%); }
                      }
                      @media (max-width: 576px) {
                        .hpwp-flag-inner {
                          grid-template-columns: 1fr;
                          text-align: center;
                          gap: 12px;
                        }
                        .hpwp-flag-badge {
                          margin: 0 auto;
                        }
                      }
                    `}
                  </style>
                  <div className="hpwp-flag-hero" style={{ margin: '12px 0 26px' }}>
                    <div className="hpwp-flag-inner">
                      <div className="hpwp-flag-badge">
                        <img src={regionFlag} alt={`${region} flag`} />
                      </div>
                      <div>
                        <h5 className="hpwp-flag-title">Your Region</h5>
                        <p className="hpwp-flag-sub">Registration is tailored to your region.</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}

              <div className="register-tab">
                <nav>
                  <div className="nav nav-tabs justify-content-center" id="nav-tab" role="tablist">
                    <button
                      className="nav-link active"
                      id="nav-home-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#nav-home"
                      type="button"
                      role="tab"
                      aria-controls="nav-home"
                      aria-selected="true"
                    >
                      Candidate (Job Seeker)
                    </button>
                    <button
                      className="nav-link"
                      id="nav-profile-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#nav-profile"
                      type="button"
                      role="tab"
                      aria-controls="nav-profile"
                      aria-selected="false"
                    >
                      Hospital / Employer
                    </button>
                  </div>
                </nav>

                <div className="tab-content mt-40" id="nav-tabContent">
                  {/* ── Candidate Tab ──────────────────────────────────────── */}
                  <div className="tab-pane fade show active" id="nav-home" role="tabpanel" aria-labelledby="nav-home-tab">
                    <form onSubmit={handleUserSubmit} noValidate>
                      {error && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                          {error}
                          <button type="button" className="btn-close" onClick={() => setError('')}></button>
                        </div>
                      )}

                      <div className="row g-4">
                        <div className="col-md-6">
                          <div className="form-inner">
                            <label htmlFor="firstname">First Name *</label>
                            <div className="input-area">
                              <img src="assets/images/icon/user-2.svg" alt="" />
                              <input
                                type="text"
                                id="firstname"
                                name="firstName"
                                placeholder="Robert"
                                value={userForm.firstName}
                                onChange={handleUserChange}
                                className={fieldErrors.firstName ? 'is-invalid' : ''}
                              />
                            </div>
                            {fieldErrors.firstName && <small className="text-danger">{fieldErrors.firstName}</small>}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-inner">
                            <label htmlFor="lastname">Last Name *</label>
                            <div className="input-area">
                              <img src="assets/images/icon/user-2.svg" alt="" />
                              <input
                                type="text"
                                id="lastname"
                                name="lastName"
                                placeholder="Johnson"
                                value={userForm.lastName}
                                onChange={handleUserChange}
                                className={fieldErrors.lastName ? 'is-invalid' : ''}
                              />
                            </div>
                            {fieldErrors.lastName && <small className="text-danger">{fieldErrors.lastName}</small>}
                          </div>
                        </div>

                        <div className="col-md-12">
                          <div className="form-inner">
                            <label htmlFor="email">Email *</label>
                            <div className="input-area">
                              <img src="assets/images/icon/email-2.svg" alt="" />
                              <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="info@example.com"
                                value={userForm.email}
                                onChange={handleUserChange}
                                className={fieldErrors.email ? 'is-invalid' : ''}
                              />
                            </div>
                            {fieldErrors.email && <small className="text-danger">{fieldErrors.email}</small>}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-inner">
                            <label htmlFor="password">Password *</label>
                            <div className="input-area position-relative">
                              <input
                                type="password"
                                id="password"
                                name="password"
                                placeholder="••••••••"
                                value={userForm.password}
                                onChange={handleUserChange}
                                className={fieldErrors.password ? 'is-invalid' : ''}
                              />
                              <i
                                className="bi bi-eye-slash position-absolute end-0 top-50 translate-middle-y pe-3 cursor-pointer"
                                id="togglePassword"
                                onClick={() => togglePasswordVisibility('password')}
                              />
                            </div>
                            {fieldErrors.password && <small className="text-danger">{fieldErrors.password}</small>}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-inner">
                            <label htmlFor="confirmPassword">Confirm Password *</label>
                            <div className="input-area position-relative">
                              <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="••••••••"
                                value={userForm.confirmPassword}
                                onChange={handleUserChange}
                                className={fieldErrors.confirmPassword ? 'is-invalid' : ''}
                              />
                              <i
                                className="bi bi-eye-slash position-absolute end-0 top-50 translate-middle-y pe-3 cursor-pointer"
                                id="toggleConfirmPassword"
                                onClick={() => togglePasswordVisibility('confirmPassword')}
                              />
                            </div>
                            {fieldErrors.confirmPassword && (
                              <small className="text-danger">{fieldErrors.confirmPassword}</small>
                            )}
                          </div>
                        </div>

                        <div className="col-12">
                          <button className="primry-btn-2 w-100" type="submit" disabled={loading}>
                            {loading ? 'Creating account...' : 'Sign Up as Candidate'}
                          </button>
                        </div>

                        <div className="col-12 text-center mt-3">
                          <h6>
                            Already have an account? <Link to="/login">Login here</Link>
                          </h6>
                        </div>
                      </div>
                    </form>
                  </div>

                  {/* ── Hospital Tab ───────────────────────────────────────── */}
                  <div className="tab-pane fade" id="nav-profile" role="tabpanel" aria-labelledby="nav-profile-tab">
                    <HospitalRegistration />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
//                     HOSPITAL REGISTRATION
// ────────────────────────────────────────────────────────────────
function HospitalRegistration() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { region } = useRegion();

  const [form, setForm] = useState({
    name: '',
    type: '',
    otherType: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: '',
    country: '',
    state: '',
    city: '',
    zip: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const hospitalTypeOptions = [
    'General Hospital',
    'Eye Hospital',
    'Neuro Hospital',
    'Children Hospital',
    'Cardiac Hospital',
    'Diagnostic Center',
    'Other',
  ];

  const stateLabel = getRegionStateLabel(region);
  const countryOptions = region === 'af' ? getRegionCountryOptions(region) : [];
  const selectedCountry = region === 'af' ? getRegionCountryRecord(region, form.country) : null;
  const stateOptions = getRegionStateOptions(region, selectedCountry);
  const selectedState = getRegionStateRecord(region, form.state, selectedCountry);
  const cityOptions = getRegionCityOptions(region, selectedState, selectedCountry);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'zip' ? value.replace(/\D/g, '') : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };

  const handleDropdown = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'country' ? { state: '', city: '' } : {}),
      ...(field === 'state' ? { city: '' } : {}),
    }));
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    setError('');
  };

  const handleHospitalSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    const errors = {};
    if (!form.name.trim()) errors.name = 'Hospital name is required';
    if (!form.email.trim()) errors.email = 'Email is required';
    if (!form.password) errors.password = 'Password is required';
    else if (form.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (form.password !== form.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (form.type === 'Other' && !form.otherType.trim()) errors.type = 'Please specify hospital type';
    if (region === 'af' && !form.country.trim()) errors.country = 'Country is required';
                    if (region === 'af' && !form.country.trim()) errors.country = 'Country is required';
                    if (!form.state.trim()) errors.state = `${stateLabel} is required`;
    if (!form.city.trim()) errors.city = 'City is required';
    if (!form.zip.trim()) errors.zip = 'Zip code is required';
    else if (!/^\d+$/.test(form.zip.trim())) errors.zip = 'Zip code must contain only numbers';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please correct the errors');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        type: form.type === 'Other' ? form.otherType.trim() : form.type,
        email: form.email.trim(),
        password: form.password,
        address: form.address?.trim() || undefined,
        country: region === 'af' ? form.country || undefined : undefined,
        country: region === 'af' ? form.country || undefined : undefined,
        state: form.state || undefined,
        city: form.city || undefined,
        zip: form.zip?.trim() ? Number(form.zip.trim()) : undefined,
      };

      await dispatch(registerHospital(payload)).unwrap();
      toast.success('Hospital registered successfully! Check your email for OTP verification.');
      navigate(`/verify-email?email=${encodeURIComponent(form.email.trim())}&role=hospital`);
    } catch (err) {
      let msg = 'Registration failed';

      if (err?.response?.status === 409 || err?.response?.data?.message?.includes('duplicate')) {
        msg = 'This email is already registered';
      } else if (err?.response?.status === 400 || err?.response?.status === 422) {
        msg = err.response.data.message || 'Invalid data provided';
      } else if (!err.response) {
        msg = 'Cannot connect to server';
      }

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleHospitalSubmit} noValidate>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      <div className="row g-4">
        <div className="col-lg-6">
          <div className="form-inner">
            <label>Hospital / Facility Name *</label>
            <input
              type="text"
              placeholder="e.g. City General Hospital"
              value={form.name}
              name="name"
              onChange={handleChange}
              className={fieldErrors.name ? 'is-invalid' : ''}
            />
            {fieldErrors.name && <small className="text-danger">{fieldErrors.name}</small>}
          </div>
        </div>

        <div className="col-lg-6">
          <label>Hospital Type *</label>
          <SearchableDropdown
            options={hospitalTypeOptions}
            placeholder="Select type"
            value={form.type}
            onChange={(val) => handleDropdown('type', val)}
          />
          {form.type === 'Other' && (
            <>
              <input
                type="text"
                className={`form-control mt-2 ${fieldErrors.type ? 'is-invalid' : ''}`}
                placeholder="Specify type (e.g. Oncology Center)"
                value={form.otherType}
                onChange={(e) => setForm((p) => ({ ...p, otherType: e.target.value }))}
              />
              {fieldErrors.type && <small className="text-danger">{fieldErrors.type}</small>}
            </>
          )}
        </div>

        <div className="col-lg-6">
          <div className="form-inner">
            <label>Email *</label>
            <input
              type="email"
              placeholder="hospital@example.com"
              value={form.email}
              name="email"
              onChange={handleChange}
              className={fieldErrors.email ? 'is-invalid' : ''}
            />
            {fieldErrors.email && <small className="text-danger">{fieldErrors.email}</small>}
          </div>
        </div>

        <div className="col-lg-6">
          <div className="form-inner">
            <label>Address</label>
            <input
              type="text"
              placeholder="Street address"
              value={form.address}
              name="address"
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="col-lg-6">
          <div className="form-inner">
            <label>Password *</label>
            <input
              type="password"
              placeholder="Enter password"
              value={form.password}
              name="password"
              onChange={handleChange}
              className={fieldErrors.password ? 'is-invalid' : ''}
            />
            {fieldErrors.password && <small className="text-danger">{fieldErrors.password}</small>}
          </div>
        </div>

        <div className="col-lg-6">
          <div className="form-inner">
            <label>Confirm Password *</label>
            <input
              type="password"
              placeholder="Confirm password"
              value={form.confirmPassword}
              name="confirmPassword"
              onChange={handleChange}
              className={fieldErrors.confirmPassword ? 'is-invalid' : ''}
            />
            {fieldErrors.confirmPassword && (
              <small className="text-danger">{fieldErrors.confirmPassword}</small>
            )}
          </div>
        </div>

        {region === 'af' ? (
          <div className="col-lg-6">
            <label>Country *</label>
            <SearchableDropdown
              options={countryOptions}
              placeholder="Select country"
              value={form.country}
              onChange={(val) => handleDropdown('country', val)}
            />
            {fieldErrors.country && <small className="text-danger">{fieldErrors.country}</small>}
          </div>
        ) : null}
        <div className="col-lg-6">
          <label>{stateLabel} *</label>
          <SearchableDropdown
            options={stateOptions}
            placeholder={`Select ${stateLabel.toLowerCase()}`}
            value={form.state}
            onChange={(val) => handleDropdown('state', val)}
            disabled={region === 'af' && !form.country}
          />
          {fieldErrors.state && <small className="text-danger">{fieldErrors.state}</small>}
        </div>

        <div className="col-lg-6">
          <label>City *</label>
          <SearchableDropdown
            options={cityOptions}
            placeholder="Select city"
            value={form.city}
            onChange={(val) => handleDropdown('city', val)}
            disabled={!form.state}
          />
          {fieldErrors.city && <small className="text-danger">{fieldErrors.city}</small>}
        </div>

        <div className="col-lg-6">
          <div className="form-inner">
            <label>Zip Code *</label>
            <input
              type="text"
              placeholder="Zip / Postal code"
              value={form.zip}
              name="zip"
              onChange={handleChange}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              className={fieldErrors.zip ? 'is-invalid' : ''}
            />
            {fieldErrors.zip && <small className="text-danger">{fieldErrors.zip}</small>}
          </div>
        </div>

        <div className="col-12 mt-4">
          <button className="primry-btn-2 w-100" type="submit" disabled={loading}>
            {loading ? 'Registering...' : 'Register Hospital'}
          </button>
        </div>
      </div>
    </form>
  );
}

export default Registration;
